type AppLanguageSupport = {
  full: string;
  short: string;
  international: string;
  /**
   * Canonical tag the localization files and the API use, when it differs from
   * our lowercase key (`pt-br` -> `pt-BR`, `zh-hans` -> `zh-Hans`).
   */
  localizationCode?: string;
  /** Detected locale codes that resolve to this language. */
  aliases?: readonly string[];
};

export const APP_LANGUAGE_SUPPORTS = {
  en: {full: 'English', short: 'eng', international: 'English'},
  es: {full: 'Español', short: 'esp', international: 'Spanish'},
  'pt-br': {
    full: 'Português (Brasil)',
    short: 'por',
    international: 'Portugese',
    localizationCode: 'pt-BR',
    aliases: ['pt'],
  },
  de: {full: 'Deutsch', short: 'deu', international: 'German'},
  tr: {full: 'Türkçe', short: 'tür', international: 'Turkish'},
  ja: {full: '日本語', short: '日', international: 'Japanese'},
  id: {full: 'Bahasa Indonesia', short: 'ind', international: 'Indonesian'},
  fr: {full: 'Français', short: 'fra', international: 'French'},
  it: {full: 'Italiano', short: 'ita', international: 'Italian'},
  th: {full: 'ไทย', short: 'ไทย', international: 'Thai'},
  ca: {full: 'Català', short: 'cat', international: 'Catalan'},
  'zh-hans': {
    full: '简体中文',
    short: '简',
    international: 'Chinese Simplified',
    localizationCode: 'zh-Hans',
    aliases: ['zh', 'zh-cn', 'zh-sg'],
  },
  'zh-hant': {
    full: '繁體中文',
    short: '繁',
    international: 'Chinese Traditional',
    localizationCode: 'zh-Hant',
    aliases: ['zh-tw', 'zh-hk', 'zh-mo'],
  },
  cs: {full: 'Čeština', short: 'čes', international: 'Czech'},
  nl: {full: 'Nederlands', short: 'ned', international: 'Dutch'},
  fil: {
    full: 'Filipino',
    short: 'fil',
    international: 'Filipino',
    aliases: ['tl'],
  },
  hi: {full: 'हिन्दी', short: 'हिं', international: 'Hindi'},
  ko: {full: '한국어', short: '한', international: 'Korean'},
  ms: {full: 'Bahasa Melayu', short: 'mel', international: 'Malay'},
  pl: {full: 'Polski', short: 'pol', international: 'Polish'},
  ro: {full: 'Română', short: 'rom', international: 'Romanian'},
  ru: {full: 'Русский', short: 'рус', international: 'Russian'},
  sv: {full: 'Svenska', short: 'sve', international: 'Swedish'},
  uk: {
    full: 'Українська',
    short: 'укр',
    international: 'Ukrainian',
  },
  vi: {
    full: 'Tiếng Việt',
    short: 'vie',
    international: 'Vietnamese',
  },
  nb: {
    full: 'Norsk bokmål',
    short: 'nor',
    international: 'Bokmal (Norwegian)',
    aliases: ['no', 'nn'],
  },
  da: {full: 'Dansk', short: 'dan', international: 'Danish'},
  el: {full: 'Ελληνικά', short: 'ελλ', international: 'Greek'},
  fi: {full: 'Suomi', short: 'fin', international: 'Finnish'},
  hu: {full: 'Magyar', short: 'hun', international: 'Hungarian'},
} as const satisfies Record<string, AppLanguageSupport>;

export type AppLanguage = keyof typeof APP_LANGUAGE_SUPPORTS;

export const APP_LANGUAGE_SUPPORTS_KEYS = Object.keys(
  APP_LANGUAGE_SUPPORTS
) as AppLanguage[];

const APP_LANGUAGE_SUPPORTS_SET = new Set<string>(APP_LANGUAGE_SUPPORTS_KEYS);

const APP_LANGUAGE_ALIASES = new Map<string, AppLanguage>(
  Object.entries<AppLanguageSupport>(APP_LANGUAGE_SUPPORTS).flatMap(
    ([language, {aliases}]) =>
      aliases?.map((alias) => [alias, language as AppLanguage] as const) ?? []
  )
);

export function isAppLanguage(value: string): value is AppLanguage {
  return APP_LANGUAGE_SUPPORTS_SET.has(value);
}

export function getCountyNameByCode(language: AppLanguage) {
  return APP_LANGUAGE_SUPPORTS[language].full;
}

export function getCountyShortNameByCode(language: AppLanguage) {
  return APP_LANGUAGE_SUPPORTS[language].short;
}

/**
 * Tag to pass to the API. Our keys are lowercase (i18next runs with
 * `lowerCaseLng`), while the backend expects the canonical form the
 * localization files are named after.
 */
export function getLocalizationCode(language: AppLanguage) {
  const {localizationCode}: AppLanguageSupport =
    APP_LANGUAGE_SUPPORTS[language];

  return localizationCode ?? language;
}

export function inferAppLanguage(lang: string, defaultValue?: AppLanguage) {
  if (!isAppLanguage(lang)) {
    return defaultValue;
  }

  return lang;
}

function normalizeDetectedAppLanguageCode(language: string): string {
  const normalizedLanguage = language.toLowerCase();

  if (isAppLanguage(normalizedLanguage)) {
    return normalizedLanguage;
  }

  const aliasedLanguage = APP_LANGUAGE_ALIASES.get(normalizedLanguage);

  if (aliasedLanguage) {
    return aliasedLanguage;
  }

  const [baseLanguage] = normalizedLanguage.split('-');

  return baseLanguage ?? normalizedLanguage;
}

export function resolveSupportedAppLanguage(
  languageCodes: readonly string[]
): AppLanguage | undefined {
  for (const languageCode of languageCodes) {
    const normalizedLanguageCode =
      normalizeDetectedAppLanguageCode(languageCode);

    if (isAppLanguage(normalizedLanguageCode)) {
      return normalizedLanguageCode;
    }
  }
}
